import {
  mockUsers,
  mockRecruiters,
  mockDashboardStats,
  mockPayments,
  mockAuditLogs,
  mockStudents,
  mockCommissions,
  mockRecruiterProfile,
  mockNotifications,
  mockAdminNotifications,
  mockPaymentUploads,
  mockPaymentUploadDetails,
  mockFinancialStats,
  mockRecruiterPayments
} from './mockData';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Mock API service for testing frontend without backend
const USE_MOCK_DATA = false; // Set to false when backend is ready

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const mockResponse = (data) => ({
  data,
  status: 200,
  statusText: 'OK'
});

// Helper function to make API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    
    try {
      const error = await response.json();
      errorMessage = error.error || errorMessage;
    } catch (parseError) {
      // If JSON parsing fails, check for rate limit
      if (response.status === 429) {
        errorMessage = 'Too many requests, please try again later.';
      }
    }
    
    throw new Error(errorMessage);
  }
  
  const data = await response.json();
  return { data }; // Wrap in data property to match mock API format
};

export const authAPI = {
  login: async (credentials) => {
    if (USE_MOCK_DATA) {
      await delay(500); // Simulate network delay
      const { username, password } = credentials;
      
      // Mock login validation
      if ((username === 'admin' && password === 'admin') || 
          (username === 'john' && password === 'john') ||
          (username === 'finance' && password === 'finance')) {
        const user = username === 'admin' ? mockUsers.admin : 
                    username === 'finance' ? mockUsers.financial_officer : 
                    mockUsers.recruiter;
        const token = 'mock-jwt-token-' + Date.now();
        
        return mockResponse({
          token,
          user
        });
      } else {
        throw new Error('Invalid credentials');
      }
    }
    
    // Real API call
    return await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  },
  
  login2FA: async (userId, code) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      if (code === '123456') {
        const user = mockUsers.recruiter;
        const token = 'mock-jwt-token-' + Date.now();
        
        return mockResponse({
          token,
          user
        });
      } else {
        throw new Error('Invalid verification code');
      }
    }
    
    // Real API call
    return await apiCall('/auth/login/2fa', {
      method: 'POST',
      body: JSON.stringify({ userId, code })
    });
  },
  
  changePassword: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Password changed successfully'
      });
    }
    
    // Real API call
    return await apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  testSmtp: async () => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        success: true,
        message: 'SMTP connection verified successfully (mock)',
        config: {
          host: 'smtp.gmail.com',
          port: '587',
          from: 'mock@example.com'
        }
      });
    }
    
    // Real API call
    return await apiCall('/auth/test-smtp');
  },

  sendTestEmail: async (email) => {
    if (USE_MOCK_DATA) {
      await delay(1000);
      return mockResponse({
        success: true,
        message: `Test email sent successfully to ${email} (mock)`,
        messageId: 'mock-message-id-' + Date.now()
      });
    }
    
    // Real API call
    return await apiCall('/auth/test-email', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  logout: async () => {
    // Real API call
    return await apiCall('/auth/logout', {
      method: 'POST'
    });
  },

  forgotPassword: async (email) => {
    if (USE_MOCK_DATA) {
      await delay(1000);
      return mockResponse({
        success: true,
        message: 'If an account with this email exists, a password reset link has been sent.'
      });
    }
    
    // Real API call
    return await apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  resetPassword: async (email, resetToken, newPassword) => {
    if (USE_MOCK_DATA) {
      await delay(1000);
      if (resetToken === '123456') {
        return mockResponse({
          success: true,
          message: 'Password has been reset successfully. You can now login with your new password.'
        });
      } else {
        throw new Error('Invalid or expired reset code');
      }
    }
    
    // Real API call
    return await apiCall('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, resetToken, newPassword })
    });
  },

  verifyDevice: async (userId, deviceFingerprint, code, trustDevice = false) => {
    if (USE_MOCK_DATA) {
      await delay(1000);
      if (code === '123456') {
        const user = mockUsers.recruiter;
        const token = 'mock-jwt-token-' + Date.now();
        
        return mockResponse({
          token,
          user
        });
      } else {
        throw new Error('Invalid or expired verification code');
      }
    }
    
    // Real API call
    return await apiCall('/auth/verify-device', {
      method: 'POST',
      body: JSON.stringify({ userId, deviceFingerprint, code, trustDevice })
    });
  }
};

export const recruiterAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockRecruiters);
    }
    
    // Real API call
    return await apiCall('/recruiters');
  },
  
  getProfile: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockRecruiterProfile);
    }
    
    // Real API call
    return await apiCall('/recruiters/profile');
  },
  
  create: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        message: 'Recruiter created successfully',
        referralCode: 'OUK' + Math.random().toString(36).substr(2, 8).toUpperCase(),
        username: data.email.split('@')[0]
      });
    }
    
    // Real API call
    return await apiCall('/recruiters', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  update: async (id, data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Recruiter updated successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/recruiters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  
  updateProfile: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Profile updated successfully'
      });
    }
    
    // Real API call
    return await apiCall('/recruiters/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  
  delete: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Recruiter deleted successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/recruiters/${id}`, {
      method: 'DELETE'
    });
  },
  
  bulkUpload: async (formData) => {
    if (USE_MOCK_DATA) {
      await delay(1000);
      return mockResponse({
        success: [
          { row: 1, email: 'test1@example.com', referralCode: 'OUK12345' },
          { row: 2, email: 'test2@example.com', referralCode: 'OUK67890' }
        ],
        errors: [
          { row: 3, error: 'Missing required fields' }
        ]
      });
    }
    
    // For FormData uploads, we need to handle authentication manually
    // because we can't use the standard apiCall function (it sets Content-Type)
    const token = localStorage.getItem('token');
    
    const config = {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
        // Don't set Content-Type - let browser set it for FormData with boundary
      },
      body: formData
    };

    const response = await fetch(`${API_BASE_URL}/recruiters/bulk-upload`, config);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data }; // Wrap in data property to match mock API format
  }
};

export const studentAPI = {
  getMyStudents: async () => {
    // Real API call
    return await apiCall('/students/my-students');
  },
  
  getCommissions: async () => {
    // Real API call
    return await apiCall('/students/commissions');
  },

  getMonthlyPerformance: async () => {
    return await apiCall('/students/monthly-performance');
  },

  getProfileStats: async () => {
    return await apiCall('/students/profile-stats');
  },

  getLeaderboard: async () => {
    return await apiCall('/students/leaderboard');
  },
  
  // Admin methods
  getAllStudents: async () => {
    // Real API call
    return await apiCall('/students/all');
  },
  
  getAdmittedStudents: async () => {
    // Real API call
    return await apiCall('/students/admitted');
  },
  
  addAdmission: async (data) => {
    // Real API call
    return await apiCall('/students/admissions', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  addEnrollment: async (data) => {
    // Real API call
    return await apiCall('/students/enrollments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};

export const paymentAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockPayments);
    }
    
    // Real API call
    return await apiCall('/payments');
  },
  
  create: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Payment record created',
        id: Math.floor(Math.random() * 1000)
      });
    }
    
    // Real API call
    return await apiCall('/payments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  update: async (id, data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Payment updated successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
};

export const reportAPI = {
  getDashboard: async () => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse(mockDashboardStats);
    }
    
    // Real API call
    return await apiCall('/reports/dashboard');
  },
  
  exportRecruiters: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockRecruiters);
    }
    
    // Real API call
    return await apiCall('/reports/recruiters/export');
  }
};

export const auditAPI = {
  getLogs: async (params) => {
    // Real API call
    const queryParams = new URLSearchParams(params).toString();
    return await apiCall(`/audit/logs${queryParams ? '?' + queryParams : ''}`);
  }
};

export const notificationAPI = {
  getAll: async (params = {}) => {
    // Real API call
    const queryParams = new URLSearchParams(params).toString();
    return await apiCall(`/notifications${queryParams ? '?' + queryParams : ''}`);
  },
  
  markAsRead: async (id) => {
    // Real API call
    return await apiCall(`/notifications/${id}/read`, {
      method: 'PUT'
    });
  },
  
  markAllAsRead: async (isAdmin = false) => {
    // Real API call
    return await apiCall('/notifications/mark-all-read', {
      method: 'PUT',
      body: JSON.stringify({ admin: isAdmin })
    });
  },
  
  getRecent: async (limit = 4, isAdmin = false) => {
    // Real API call
    return await apiCall(`/notifications/recent?limit=${limit}&admin=${isAdmin}`);
  }
};

export const twoFAAPI = {
  getStatus: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        enabled: false,
        email: 'test@example.com'
      });
    }
    
    // Real API call
    return await apiCall('/2fa/status');
  },
  
  sendSetupCode: async () => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        success: true,
        message: 'Verification code sent to your email',
        expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString()
      });
    }
    
    // Real API call
    return await apiCall('/2fa/setup/send-code', {
      method: 'POST'
    });
  },
  
  verifySetup: async (code) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      if (code === '123456') {
        return mockResponse({
          success: true,
          message: '2FA enabled successfully',
          backupCodes: ['ABC123', 'DEF456', 'GHI789', 'JKL012', 'MNO345']
        });
      } else {
        throw new Error('Invalid verification code');
      }
    }
    
    // Real API call
    return await apiCall('/2fa/setup/verify', {
      method: 'POST',
      body: JSON.stringify({ code })
    });
  },
  
  disable: async (password) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        success: true,
        message: '2FA disabled successfully'
      });
    }
    
    // Real API call
    return await apiCall('/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password })
    });
  },
  
  sendLoginCode: async (email) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        success: true,
        message: 'Verification code sent to your email',
        expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString()
      });
    }
    
    // Real API call
    return await apiCall('/2fa/login/send-code', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },
  
  verifyLogin: async (email, code) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      if (code === '123456') {
        return mockResponse({
          success: true,
          message: 'Code verified successfully',
          userId: 1
        });
      } else {
        throw new Error('Invalid verification code');
      }
    }
    
    // Real API call
    return await apiCall('/2fa/login/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code })
    });
  }
};

export const userAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse([
        {
          id: 1,
          username: 'admin',
          email: 'admin@ouk.ac.ke',
          role: 'admin',
          is_active: true,
          must_change_password: false,
          created_at: '2024-01-01T00:00:00Z'
        },
        {
          id: 2,
          username: 'finance',
          email: 'finance@ouk.ac.ke',
          role: 'financial_officer',
          is_active: true,
          must_change_password: true,
          created_at: '2024-01-02T00:00:00Z'
        }
      ]);
    }
    
    // Real API call
    return await apiCall('/users');
  },
  
  create: async (userData) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        message: 'User created successfully',
        user: {
          id: Math.floor(Math.random() * 1000),
          ...userData
        },
        emailSent: true
      });
    }
    
    // Real API call
    return await apiCall('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },
  
  update: async (id, userData) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'User updated successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },
  
  delete: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'User deleted successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/users/${id}`, {
      method: 'DELETE'
    });
  }
};

export const deviceAPI = {
  getMyDevices: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        devices: [
          {
            id: 1,
            device_name: 'Chrome on Windows',
            browser: 'Chrome 120.0',
            os: 'Windows 10',
            ip_address: '192.168.1.100',
            is_trusted: true,
            last_used_at: '2024-01-15T10:30:00Z',
            created_at: '2024-01-10T08:00:00Z'
          },
          {
            id: 2,
            device_name: 'Safari on iPhone',
            browser: 'Safari 17.0',
            os: 'iOS 17.2',
            ip_address: '192.168.1.101',
            is_trusted: false,
            last_used_at: '2024-01-14T15:45:00Z',
            created_at: '2024-01-14T15:45:00Z'
          }
        ]
      });
    }
    
    // Real API call
    return await apiCall('/devices/my-devices');
  },

  removeDevice: async (deviceId) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Device removed successfully'
      });
    }
    
    // Real API call
    return await apiCall(`/devices/my-devices/${deviceId}`, {
      method: 'DELETE'
    });
  },

  getLoginAttempts: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        attempts: [
          {
            device_fingerprint: 'abc123',
            ip_address: '192.168.1.100',
            user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            success: true,
            failure_reason: null,
            requires_device_verification: false,
            created_at: '2024-01-15T10:30:00Z'
          },
          {
            device_fingerprint: 'def456',
            ip_address: '192.168.1.101',
            user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_2 like Mac OS X)',
            success: false,
            failure_reason: 'Device verification required',
            requires_device_verification: true,
            created_at: '2024-01-14T15:45:00Z'
          }
        ]
      });
    }
    
    // Real API call
    return await apiCall('/devices/login-attempts');
  }
};

export const roleAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse([
        {
          id: 1,
          name: 'admin',
          display_name: 'Administrator',
          description: 'Full system access with all permissions',
          is_system_role: true,
          user_count: 1,
          permission_count: 25
        },
        {
          id: 2,
          name: 'financial_officer',
          display_name: 'Financial Officer',
          description: 'Access to financial data, payments, and reports',
          is_system_role: true,
          user_count: 2,
          permission_count: 12
        },
        {
          id: 3,
          name: 'recruiter',
          display_name: 'Recruiter',
          description: 'Basic recruiter access to manage students and view commissions',
          is_system_role: true,
          user_count: 15,
          permission_count: 5
        }
      ]);
    }
    
    return await apiCall('/roles');
  },

  getById: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        id: 1,
        name: 'admin',
        display_name: 'Administrator',
        description: 'Full system access with all permissions',
        is_system_role: true,
        permissions: []
      });
    }
    
    return await apiCall(`/roles/${id}`);
  },

  create: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        id: Math.floor(Math.random() * 1000),
        message: 'Role created successfully',
        ...data
      });
    }
    
    return await apiCall('/roles', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  createWithPermissions: async (roleData, permissions) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        id: Math.floor(Math.random() * 1000),
        message: 'Role created successfully with permissions',
        ...roleData
      });
    }
    
    return await apiCall('/roles/with-permissions', {
      method: 'POST',
      body: JSON.stringify({ ...roleData, permissions })
    });
  },

  update: async (id, data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Role updated successfully'
      });
    }
    
    return await apiCall(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  delete: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Role deleted successfully'
      });
    }
    
    return await apiCall(`/roles/${id}`, {
      method: 'DELETE'
    });
  },

  updatePermissions: async (id, permissions) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        message: 'Role permissions updated successfully'
      });
    }
    
    return await apiCall(`/roles/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions })
    });
  }
};

export const permissionAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        'Dashboard': [
          { id: 1, name: 'dashboard.view', display_name: 'View Dashboard', description: 'Access to main dashboard' },
          { id: 2, name: 'dashboard.analytics', display_name: 'View Analytics', description: 'Access to dashboard analytics' }
        ],
        'User Management': [
          { id: 3, name: 'users.view', display_name: 'View Users', description: 'View user list and details' },
          { id: 4, name: 'users.create', display_name: 'Create Users', description: 'Create new user accounts' }
        ]
      });
    }
    
    return await apiCall('/permissions');
  },

  getList: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse([]);
    }
    
    return await apiCall('/permissions/list');
  },

  getById: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        id: 1,
        name: 'dashboard.view',
        display_name: 'View Dashboard',
        description: 'Access to main dashboard',
        category: 'Dashboard',
        resource: 'dashboard',
        action: 'view'
      });
    }
    
    return await apiCall(`/permissions/${id}`);
  },

  create: async (data) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      return mockResponse({
        id: Math.floor(Math.random() * 1000),
        message: 'Permission created successfully',
        ...data
      });
    }
    
    return await apiCall('/permissions', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id, data) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Permission updated successfully'
      });
    }
    
    return await apiCall(`/permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  delete: async (id) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse({
        message: 'Permission deleted successfully'
      });
    }
    
    return await apiCall(`/permissions/${id}`, {
      method: 'DELETE'
    });
  },

  getCategories: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse([
        'Dashboard',
        'User Management',
        'Recruiter Management',
        'Student Management',
        'Payment Management',
        'Reports',
        'System Administration',
        'Profile'
      ]);
    }
    
    return await apiCall('/permissions/categories/list');
  }
};

// For when you want to switch back to real API
const api = null; // Will be axios instance when USE_MOCK_DATA is false

export default api;
export const paymentUploadAPI = {
  getAll: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockPaymentUploads);
    }
    
    // Real API call
    return await apiCall('/payments/uploads');
  },
  
  getDetails: async (uploadId) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      const details = mockPaymentUploadDetails.filter(d => d.upload_id === uploadId);
      return mockResponse(details);
    }
    
    // Real API call
    return await apiCall(`/payments/uploads/${uploadId}/details`);
  },
  
  upload: async (formData) => {
    if (USE_MOCK_DATA) {
      await delay(2000); // Simulate processing time
      
      // Simulate CSV processing
      const newUpload = {
        id: mockPaymentUploads.length + 1,
        uploaded_by: 3,
        filename: formData.get('file')?.name || 'uploaded_file.csv',
        total_records: Math.floor(Math.random() * 30) + 10,
        successful_records: 0,
        failed_records: 0,
        total_amount: 0,
        upload_status: 'processing',
        upload_date: new Date().toISOString(),
        processed_at: null,
        notes: 'Processing payment upload...'
      };
      
      // Simulate processing completion after a delay
      setTimeout(() => {
        newUpload.upload_status = 'completed';
        newUpload.successful_records = newUpload.total_records - Math.floor(Math.random() * 3);
        newUpload.failed_records = newUpload.total_records - newUpload.successful_records;
        newUpload.total_amount = newUpload.successful_records * (Math.floor(Math.random() * 10000) + 5000);
        newUpload.processed_at = new Date().toISOString();
        newUpload.notes = `Processed ${newUpload.successful_records} payments successfully, ${newUpload.failed_records} failed`;
      }, 3000);
      
      mockPaymentUploads.unshift(newUpload);
      
      return mockResponse({
        message: 'Payment file uploaded successfully',
        upload: newUpload
      });
    }
    
    // Real API call
    return await apiCall('/payments/upload', {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData
    });
  },
  
  getStats: async () => {
    if (USE_MOCK_DATA) {
      await delay(300);
      return mockResponse(mockFinancialStats);
    }
    
    // Real API call
    return await apiCall('/payments/stats');
  },
  
  processPayment: async (uploadId, detailId, action) => {
    if (USE_MOCK_DATA) {
      await delay(500);
      const detail = mockPaymentUploadDetails.find(d => d.id === detailId);
      if (detail) {
        if (action === 'retry') {
          detail.processing_status = 'success';
          detail.error_message = null;
        } else if (action === 'reject') {
          detail.processing_status = 'failed';
          detail.error_message = 'Manually rejected by finance officer';
        }
      }
      
      return mockResponse({
        message: `Payment ${action} successfully`,
        detail
      });
    }
    
    // Real API call
    return await apiCall(`/payments/uploads/${uploadId}/details/${detailId}/${action}`, {
      method: 'POST'
    });
  },
  
  getRecruiterPayments: async (searchTerm = '', sortBy = 'name') => {
    if (USE_MOCK_DATA) {
      await delay(300);
      let recruiters = [...mockRecruiterPayments];
      
      // Filter by search term
      if (searchTerm) {
        recruiters = recruiters.filter(r => 
          r.recruiter_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.recruiter_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.referral_code.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Sort recruiters
      recruiters.sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.recruiter_name.localeCompare(b.recruiter_name);
          case 'total_earned':
            return b.total_commissions_earned - a.total_commissions_earned;
          case 'total_paid':
            return b.total_paid - a.total_paid;
          case 'pending':
            return b.pending_amount - a.pending_amount;
          case 'last_payment':
            return new Date(b.last_payment_date || 0) - new Date(a.last_payment_date || 0);
          default:
            return 0;
        }
      });
      
      return mockResponse(recruiters);
    }
    
    // Real API call
    const params = new URLSearchParams({ search: searchTerm, sort: sortBy }).toString();
    return await apiCall(`/payments/recruiters${params ? '?' + params : ''}`);
  },
  
  getRecruiterPaymentDetails: async (recruiterId) => {
    if (USE_MOCK_DATA) {
      await delay(300);
      const recruiter = mockRecruiterPayments.find(r => r.recruiter_id === parseInt(recruiterId));
      return mockResponse(recruiter || null);
    }
    
    // Real API call
    return await apiCall(`/payments/recruiters/${recruiterId}`);
  }
};